import { SimpleInput } from '@dreamstack/simple-components'
import { withStyles } from '@material-ui/core'
import filter from 'lodash/filter'
import first from 'lodash/first'
import last from 'lodash/last'
import map from 'lodash/map'
import { useMemo } from 'react'
import tw from 'twin.macro'

export type BTLCalcTableEntry = {
  houseValue: number
  loan: number
  interest: number
  totalMonthlyPayment: number
  maintenance: number
  totalRate: number
  rent: number
  cacheFlow: number
  cacheFlowCumulative: number
  advantageBuyer: number
  idemCumulative: number
  netGain?: number
  netGainAfterTax: number
}

export type BTLCalcResultsData = {
  agentFees: number
  propertyTax: number
  notaryFees: number
  priceValue: number
  appreciateValue: number
  interestRate: number
  rentValue: number
  rent: number
  rentalIncrease: number
}

type BTLCalcParamsData = {
  loanSize: number
  purchaseCosts: number
  mortgagePayment: number
  maintenanceCosts: number
}

type ChartDataSeries = {
  id: string
  data: {
    x: number
    y: number
  }[]
}

export const SimpleInputSmallMargin = withStyles((theme) => ({
  root: {
    '& label': {
      marginLeft: -18,
    },
  },
}))(SimpleInput)

export const HorizontalLine2 = tw.div`border-t w-full mt-px-24 mb-px-12 border-color[rgba(43, 45, 54, 0.2)]`
export const HorizontalLine3 = tw.div`border-t-2 w-full mt-px-12 border-color[rgba(43, 45, 54, 0.2)]`

export const FIELD_PROPS = {
  style: {
    width: 160,
    paddingLeft: 12,
    paddingTop: 24,
    fontWeight: 200,
    fontSize: 18,
  },
}

export const formatOptionsDecimal0 = {
  style: 'decimal',
  maximumFractionDigits: 0,
} as Intl.NumberFormatOptions

export const formatOptionsDecimal1 = {
  style: 'decimal',
  maximumFractionDigits: 1,
} as Intl.NumberFormatOptions

export const formatOptionsDecimal2 = {
  style: 'decimal',
  maximumFractionDigits: 2,
} as Intl.NumberFormatOptions

const fixPunctuation = (txt: string, locale: string): string => {
  if (locale === 'de') {
    return txt.replace(/\./g, '').replace(/\,/g, '.')
  } else {
    return txt.replace(/,/g, '')
  }
}

export const processUserInput = (
  text: string,
  locale: string,
  maxValue: number,
  setErrorText: Function,
  handler: Function
): void => {
  const tmp = parseFloat(fixPunctuation(text, locale))
  if (isNaN(tmp)) {
    setErrorText('not a number')
  } else {
    if (tmp < 0) {
      setErrorText('too small')
    } else if (tmp > maxValue) {
      setErrorText('too large')
    } else {
      setErrorText('')
      handler(tmp)
    }
  }
}

export function useGetTableData(
  props: BTLCalcResultsData,
  params: BTLCalcParamsData
): BTLCalcTableEntry[] {
  const { loanSize, purchaseCosts, mortgagePayment, maintenanceCosts } = params

  const tableResult = useMemo(() => {
    const table = []
    for (let i = 0; i <= 30; i++) {
      const prev: any = i > 0 ? table[i - 1] : null

      const houseValue =
        i === 0
          ? props.priceValue
          : prev.houseValue * (1 + props.appreciateValue / 100)
      const interest =
        i === 0 ? 0 : Math.max((prev.loan * (props.interestRate / 100)) / 12, 0)
      const loan =
        i === 0
          ? loanSize
          : Math.max(prev.loan - mortgagePayment + 12 * interest, 0)
      const totalMonthlyPayment = Math.min(mortgagePayment / 12, loan)
      const maintenance = (houseValue * maintenanceCosts) / 100 / 12
      const totalRate = totalMonthlyPayment + maintenance
      const rent =
        i < 2 ? props.rentValue : prev.rent * (1 + props.rentalIncrease / 100)
      const cacheFlow = rent - totalRate
      const cacheFlowCumulative =
        i === 0 ? 0 : 12 * cacheFlow + prev.cacheFlowCumulative
      const advantageBuyer = rent - interest - maintenance
      const idemCumulative = cacheFlowCumulative + loanSize - loan
      const netGain =
        i === 0
          ? -purchaseCosts
          : houseValue - props.priceValue + idemCumulative - purchaseCosts
      const netGainAfterTax = netGain

      const row = {
        houseValue,
        loan,
        interest,
        totalMonthlyPayment,
        maintenance,
        totalRate,
        rent,
        cacheFlow,
        cacheFlowCumulative,
        advantageBuyer,
        idemCumulative,
        netGain,
        netGainAfterTax,
      }
      table.push(row)
    }

    return table
  }, [
    loanSize,
    maintenanceCosts,
    mortgagePayment,
    props.appreciateValue,
    props.interestRate,
    props.priceValue,
    props.rentValue,
    props.rentalIncrease,
    purchaseCosts,
  ])

  return tableResult
}

export const useGetNetZeroYear = (
  props: BTLCalcResultsData,
  params: BTLCalcParamsData
) => {
  const tableData = useGetTableData(props, params)
  const valieTableData = filter(tableData, (row) => !isNaN(row.netGain as any))
  const netGainValues = map(valieTableData, (row) => row.netGain as number)

  const positives = netGainValues.filter((x) => x > 0)
  const negatives = netGainValues.filter((x) => x < 0)

  const lastNeg = Math.abs(last(negatives) as any)
  const firstPos = first(positives) as any

  const total = lastNeg + firstPos
  const percentOfYearTillZero = lastNeg / total

  const yearOnZero = negatives.length - 1 + percentOfYearTillZero
  return yearOnZero
}

export const useGetNetZeroYearHF = (
  props: BTLCalcResultsData,
  params: BTLCalcParamsData
) => {
  const purchaseProfitable = useGetPurchaseProfitable(props, params)
  return purchaseProfitable
}

export function useGetPurchaseProfitable(
  props: BTLCalcResultsData,
  params: BTLCalcParamsData
) {
  const { maintenanceCosts } = params
  const {
    agentFees,
    notaryFees,
    propertyTax,
    rentValue,
    priceValue,
    interestRate,
    appreciateValue,
  } = props

  const rentalReturn = (100 * (rentValue * 12)) / priceValue
  const rentalYield = rentalReturn - interestRate - maintenanceCosts

  // Hypofriend formular has a mistake? appreciation is empty field in formular, therefor we calculate with 0
  const ammortization = 0
  const purchaseProfitable =
    (propertyTax + notaryFees + agentFees) /
    (rentalYield + appreciateValue + ammortization)
  return purchaseProfitable
}
export function useGetChartData(
  props: BTLCalcResultsData,
  params: BTLCalcParamsData
): ChartDataSeries[] {
  const tableData = useGetTableData(props, params)
  const valieTableData = filter(tableData, (row) => !isNaN(row.netGain as any))
  const netGainValues = map(valieTableData, (row) => row.netGain as number)

  const positives = netGainValues.filter((x) => x > 0)
  const negatives = netGainValues.filter((x) => x < 0)

  const lastNeg = Math.abs(last(negatives) as any)
  const firstPos = first(positives) as any

  const total = lastNeg + firstPos
  const percentOfYearTillZero = lastNeg / total

  const yearOnZero = negatives.length - 1 + percentOfYearTillZero

  const yearOnZeroChartData = { x: yearOnZero, y: 0 }

  const negativesMapped = [
    ...map(negatives, (p, index) => ({ x: index, y: p })),
    yearOnZeroChartData,
  ]

  const positivesMapped = [
    yearOnZeroChartData,
    ...map(positives, (p, index) => ({
      x: index + negativesMapped.length - 1,
      y: p,
    })),
  ]

  return [
    { id: 'positive', data: positivesMapped },
    { id: 'negative', data: negativesMapped },
  ]
}
